import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { 
  Phone, 
  Mail, 
  MessageSquare, 
  CheckCircle2, 
  Sparkles,
  MapPin,
  Shield,
  Eye,
  TreePine,
  TrendingUp,
  Gem,
  Home,
  Ruler,
  Building2,
  Hammer,
  Users,
  Leaf,
  Lightbulb,
  Waves,
  Sun,
  Mountain
} from 'lucide-react';
import { submitContactForm } from '../services/mockService';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ submitted: false, loading: false });

  const handleSubmit = async (e, formType) => {
    e.preventDefault();
    setFormStatus({ submitted: false, loading: true });
    
    const result = await submitContactForm({ ...formData, formType });
    
    if (result.success) {
      setFormStatus({ submitted: true, loading: false });
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setFormStatus({ submitted: false, loading: false }), 5000);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const whatsappNumber = '+972526006003';
  const whatsappLink = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}`;

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <Gem className="logo-icon" />
              <span className="logo-text">ALECTOUS</span>
            </div>
            <nav className="nav-links">
              <a href="#why-cape-greco" className="nav-link">Location</a>
              <a href="#what-we-deliver" className="nav-link">Services</a>
              <a href="#contact" className="nav-link">Contact</a>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
                <MessageSquare size={18} />
                <span>WhatsApp</span>
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles size={16} />
            <span>Luxury Villas in Cape Greco Cyprus</span>
          </div>
          <h1 className="hero-title">
            Build A Luxury Home Tailored To Your Vision And Lifestyle
          </h1>
          <p className="hero-description">
            Alectous develops high end luxury villas in Cape Greco by combining prime land acquisition, 
            precise planning and custom architectural design to deliver exceptional private homes in Cyprus 
            most exclusive coastal location.
          </p>
          <div className="hero-cta">
            <Button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} 
                    className="primary-cta">
              Request Project Details
            </Button>
            <span className="qualified-text">For qualified buyers only</span>
          </div>
        </div>
      </section>

      {/* Why Cape Greco */}
      <section id="why-cape-greco" className="section why-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Cape Greco</h2>
            <p className="section-subtitle">Cyprus most exclusive coastal residential area</p>
          </div>
          
          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon why-icon-gradient">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Protected Natural Coastline</h3>
              <p>Open sea views in a preserved environment</p>
            </div>
            
            <div className="why-card">
              <div className="why-icon why-icon-gradient">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17l10 5 10-5M2 12l10 5 10-5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Extremely Limited Availability</h3>
              <p>One of the last truly prestigious villa locations</p>
            </div>
            
            <div className="why-card">
              <div className="why-icon why-icon-gradient">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>No High Density Developments</h3>
              <p>Privacy and exclusivity guaranteed</p>
            </div>
            
            <div className="why-card">
              <div className="why-icon why-icon-gradient">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="9 22 9 12 15 12 15 22" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Nature & Long Term Value</h3>
              <p>A level of exclusivity that cannot be recreated</p>
            </div>
          </div>

          <div className="feature-image-container">
            <img 
              src="/images/cape-greco.webp" 
              alt="Cape Greco Coastline" 
              className="feature-image"
            />
          </div>

          <div className="highlight-box">
            <Mountain className="highlight-icon" />
            <p className="highlight-text">
              Cape Greco offers a level of exclusivity that cannot be recreated elsewhere.
            </p>
          </div>
        </div>
      </section>

      {/* A Smarter Way */}
      <section className="section smarter-section">
        <div className="container">
          <div className="smarter-content">
            <div className="smarter-text">
              <h2 className="section-title">A Smarter Way to Own a Luxury Villa</h2>
              <p className="section-subtitle">More control better value superior outcome</p>
              
              <div className="benefits-list">
                <div className="benefit-item">
                  <CheckCircle2 className="benefit-icon" />
                  <div>
                    <h4>15% Cost Advantage</h4>
                    <p>Compared to comparable finished luxury villas</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <CheckCircle2 className="benefit-icon" />
                  <div>
                    <h4>Full Design Freedom</h4>
                    <p>Define layout size and spatial flow exactly as you want</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <CheckCircle2 className="benefit-icon" />
                  <div>
                    <h4>Premium Architecture</h4>
                    <p>Top architectural planning focused on lifestyle not compromise</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <CheckCircle2 className="benefit-icon" />
                  <div>
                    <h4>Long Term Asset</h4>
                    <p>Built to premium standards for lasting value</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="smarter-image">
              <img 
                src="https://images.unsplash.com/photo-1622626732808-b59f310cce62" 
                alt="Luxury Villa Pool" 
                className="feature-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Deliver */}
      <section id="what-we-deliver" className="section deliver-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What We Deliver</h2>
            <p className="section-subtitle">A fully managed luxury development experience</p>
          </div>
          
          <div className="deliver-grid">
            <Card className="deliver-card">
              <CardContent className="deliver-card-content">
                <MapPin className="deliver-icon" />
                <h3>Premium Land Acquisition</h3>
                <p>Acquisition of premium land plots in Cape Greco</p>
              </CardContent>
            </Card>
            
            <Card className="deliver-card">
              <CardContent className="deliver-card-content">
                <Building2 className="deliver-icon" />
                <h3>Planning & Permits</h3>
                <p>Full planning approvals and permits handled</p>
              </CardContent>
            </Card>
            
            <Card className="deliver-card">
              <CardContent className="deliver-card-content">
                <Ruler className="deliver-icon" />
                <h3>Custom Architecture</h3>
                <p>Custom architectural design with top design standards</p>
              </CardContent>
            </Card>
            
            <Card className="deliver-card">
              <CardContent className="deliver-card-content">
                <Hammer className="deliver-icon" />
                <h3>Luxury Construction</h3>
                <p>Luxury construction with perfect finishes</p>
              </CardContent>
            </Card>
            
            <Card className="deliver-card">
              <CardContent className="deliver-card-content">
                <Users className="deliver-icon" />
                <h3>Project Management</h3>
                <p>Full local project management and supervision</p>
              </CardContent>
            </Card>
            
            <Card className="deliver-card">
              <CardContent className="deliver-card-content">
                <CheckCircle2 className="deliver-icon" />
                <h3>Single Point of Contact</h3>
                <p>One professional team from land to completion</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Luxury Design */}
      <section className="section design-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Luxury Design and Smart Living</h2>
            <p className="section-subtitle">Refined design sustainable building and intelligent technology</p>
          </div>
          
          <div className="design-features">
            <div className="design-images-gallery-new">
              <div className="gallery-row-top">
                <div className="gallery-img-wrapper gallery-img-large">
                  <img 
                    src="/images/1.jpeg" 
                    alt="Luxury Villa Design 1" 
                    className="design-img"
                  />
                </div>
                <div className="gallery-img-wrapper gallery-img-medium">
                  <img 
                    src="/images/4.jpeg" 
                    alt="Luxury Villa Design 4" 
                    className="design-img"
                  />
                </div>
              </div>
              <div className="gallery-row-bottom">
                <div className="gallery-img-wrapper gallery-img-small">
                  <img 
                    src="/images/3.jpeg" 
                    alt="Luxury Villa Design 3" 
                    className="design-img"
                  />
                </div>
                <div className="gallery-img-wrapper gallery-img-small">
                  <img 
                    src="/images/2.jpeg" 
                    alt="Luxury Villa Design 2" 
                    className="design-img"
                  />
                </div>
                <div className="gallery-img-wrapper gallery-img-small">
                  <img 
                    src="/images/5.jpeg" 
                    alt="Luxury Villa Design 5" 
                    className="design-img"
                  />
                </div>
              </div>
            </div>
            
            <div className="design-list">
              <div className="design-item">
                <Home className="design-icon" />
                <div>
                  <h4>Timeless Architecture</h4>
                  <p>Clean modern architecture with high end materials and perfect finishes</p>
                </div>
              </div>
              
              <div className="design-item">
                <Leaf className="design-icon" />
                <div>
                  <h4>Green Building Principles</h4>
                  <p>Energy efficient construction and solar integration</p>
                </div>
              </div>
              
              <div className="design-item">
                <Lightbulb className="design-icon" />
                <div>
                  <h4>Smart Home Systems</h4>
                  <p>Intelligent control for lighting climate security and shading</p>
                </div>
              </div>
              
              <div className="design-item">
                <Sparkles className="design-icon" />
                <div>
                  <h4>AI Home Management</h4>
                  <p>Reduce energy consumption and improve daily comfort</p>
                </div>
              </div>
              
              <div className="design-item">
                <Waves className="design-icon" />
                <div>
                  <h4>Indoor Outdoor Living</h4>
                  <p>Seamless integration with pools terraces and landscaped gardens</p>
                </div>
              </div>
            </div>
          </div>
          
          <p className="design-tagline">Luxury that is elegant efficient and future ready.</p>
        </div>
      </section>

      {/* Why Alectous */}
      <section className="section alectous-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Alectous</h2>
            <p className="section-subtitle">Local expertise international standards full support</p>
          </div>
          
          <div className="alectous-grid">
            <div className="alectous-feature">
              <MapPin className="alectous-icon" />
              <h3>On-Ground Presence</h3>
              <p>Permanent presence in Cyprus with deep local knowledge</p>
            </div>
            
            <div className="alectous-feature">
              <Building2 className="alectous-icon" />
              <h3>Planning Expertise</h3>
              <p>Deep understanding of regulation and local authorities</p>
            </div>
            
            <div className="alectous-feature">
              <Gem className="alectous-icon" />
              <h3>Selective Projects</h3>
              <p>Low volume luxury projects only for quality focus</p>
            </div>
            
            <div className="alectous-feature">
              <CheckCircle2 className="alectous-icon" />
              <h3>Professional Execution</h3>
              <p>Transparent communication and delivery</p>
            </div>
          </div>
          
          <div className="support-services">
            <h3 className="support-title">Comprehensive Support Services</h3>
            <div className="support-grid">
              <div className="support-item">
                <CheckCircle2 size={20} />
                <span>Local bank account assistance</span>
              </div>
              <div className="support-item">
                <CheckCircle2 size={20} />
                <span>Cryptocurrency account setup support</span>
              </div>
              <div className="support-item">
                <CheckCircle2 size={20} />
                <span>Coordination with experienced local lawyers</span>
              </div>
              <div className="support-item">
                <CheckCircle2 size={20} />
                <span>Trusted accountants and tax advisors</span>
              </div>
            </div>
          </div>
          
          <p className="alectous-tagline">Alectous acts as a single point of contact throughout the entire journey.</p>
        </div>
      </section>

      {/* Location Map Section */}
      <section className="section map-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Location</h2>
            <p className="section-subtitle">Close to Konnos Beach</p>
          </div>
          
          <div className="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3273.7419636891344!2d34.06049631525598!3d34.97656798035631!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14e0832b0dce0001%3A0x4fbf3f8c0c8f5c5e!2sKonnos%20Beach!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Konnos Beach Location"
            ></iframe>
            <div className="map-overlay-info">
              <div className="map-info-card">
                <MapPin className="map-info-icon" />
                <div>
                  <h4>Konnos Beach Location</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Purchase Options */}
      <section className="section purchase-section">
        <div className="container">
          <div className="purchase-content">
            <h2 className="section-title">Flexible Purchase Options</h2>
            <div className="purchase-options">
              <div className="purchase-option">
                <CheckCircle2 className="purchase-icon" />
                <span>Standard bank based purchasing structures</span>
              </div>
              <div className="purchase-option">
                <CheckCircle2 className="purchase-icon" />
                <span>Cryptocurrency payments accepted subject to compliance</span>
              </div>
              <div className="purchase-option">
                <CheckCircle2 className="purchase-icon" />
                <span>Clear legal and accounting framework for each transaction</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Availability */}
      <section className="section availability-section">
        <div className="container">
          <div className="availability-box">
            <Sun className="availability-icon" />
            <h2>Current Availability</h2>
            <p>We work with a limited number of plots and villas at any given time to maintain full control quality and attention to detail.</p>
            <p className="availability-status">Availability is limited.</p>
            <Button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} 
                    className="availability-cta">
              Request Availability and Project Overview
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section contact-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h2 className="section-title">Next Step</h2>
              <p className="contact-description">
                If you are considering a luxury residence in Cape Greco or a home designed precisely around 
                your vision and lifestyle we invite you to begin with a private confidential discussion.
              </p>
              
              <div className="contact-methods">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="contact-method">
                  <MessageSquare className="contact-method-icon" />
                  <div>
                    <h4>WhatsApp</h4>
                    <p>{whatsappNumber}</p>
                  </div>
                </a>
                
                <div className="contact-method">
                  <Phone className="contact-method-icon" />
                  <div>
                    <h4>Phone</h4>
                    <p>{whatsappNumber}</p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <Mail className="contact-method-icon" />
                  <div>
                    <h4>Email Inquiry</h4>
                    <p>Fill out the form to reach us</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="contact-form-container">
              <Card className="contact-form-card">
                <CardContent className="contact-form-content">
                  <h3>Schedule a Private Introduction Call</h3>
                  <p className="form-subtitle">Qualified enquiries only</p>
                  
                  {formStatus.submitted ? (
                    <div className="form-success">
                      <CheckCircle2 className="success-icon" />
                      <h4>Thank you for your inquiry</h4>
                      <p>We will contact you shortly to discuss your luxury villa project.</p>
                    </div>
                  ) : (
                    <form onSubmit={(e) => handleSubmit(e, 'contact')} className="contact-form">
                      <div className="form-group">
                        <label htmlFor="name">Full Name</label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your.email@example.com"
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1234567890"
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="message">Message</label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your vision for your luxury villa..."
                          rows={4}
                          required
                        />
                      </div>
                      
                      <Button type="submit" className="submit-btn" disabled={formStatus.loading}>
                        {formStatus.loading ? 'Sending...' : 'Submit Inquiry'}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <Gem className="logo-icon" />
                <span className="logo-text">ALECTOUS</span>
              </div>
              <p className="footer-tagline">Luxury Villas in Cape Greco Cyprus</p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Address</h4>
                <p>1 April, 111, Paralimni, Famagusta</p>
                <p>Cyprus 5280</p>
              </div>
              
              <div className="footer-column">
                <h4>Contact</h4>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">WhatsApp</a>
                <a href={`tel:${whatsappNumber}`}>Call Us</a>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Alectous. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
